/**
 * Header strip shown at the top of every Predict tab.
 *
 * Shows:
 *   • "Last updated …" timestamp + author when a cached snapshot is
 *     present.
 *   • A prominent "Run new analysis" button that confirms before
 *     spending tokens.
 *   • An "Analyzing…" inline state while a fresh run is in flight.
 *
 * The pattern is: pages always load cached data on visit (cheap),
 * users click here to spend tokens on a fresh Claude call.
 */
import { useState } from "react";
import { Loader2, RefreshCw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";

interface Props {
  generatedAt?: string | null;
  isRunning: boolean;
  onRun: () => void | Promise<void>;
  /** Override the default confirmation copy. */
  confirmMessage?: string;
  /** Override "Run new analysis" label. */
  runLabel?: string;
  /** First-time-empty? Hide the timestamp line and emphasise CTA. */
  emptyState?: boolean;
}

const DEFAULT_CONFIRM =
  "This will generate a fresh AI analysis using your latest employee data. Continue?";

function formatTime(iso?: string | null) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return null;
  }
}

export function RunAnalysisHeader({
  generatedAt,
  isRunning,
  onRun,
  confirmMessage,
  runLabel = "Run new analysis",
  emptyState,
}: Props) {
  const [confirming, setConfirming] = useState(false);
  const stamp = formatTime(generatedAt);

  async function handleRun() {
    setConfirming(false);
    await onRun();
  }

  return (
    <Card className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-accent/10 p-2 text-accent">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          {emptyState ? (
            <>
              <div className="text-sm font-semibold">
                No analysis has been run yet
              </div>
              <p className="text-xs text-text-muted">
                Click "Run analysis" to generate the first AI snapshot.
                Subsequent visits will load this result instantly.
              </p>
            </>
          ) : (
            <>
              <div className="text-sm font-semibold">
                {isRunning ? "Analyzing…" : "Cached analysis"}
              </div>
              <p className="text-xs text-text-muted">
                {stamp
                  ? `Last updated ${stamp}`
                  : "Result loaded from cache."}
                {" · Pages load cached results to avoid spending tokens."}
              </p>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {isRunning ? (
          <Button variant="primary" disabled>
            <Loader2 className="h-4 w-4 animate-spin" />
            Analyzing…
          </Button>
        ) : confirming ? (
          <>
            <span className="hidden text-xs text-text-muted md:inline">
              {confirmMessage || DEFAULT_CONFIRM}
            </span>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleRun}>
              Confirm & run
            </Button>
          </>
        ) : (
          <Button
            variant={emptyState ? "primary" : "outline"}
            onClick={() => setConfirming(true)}
          >
            <RefreshCw className="h-4 w-4" />
            {emptyState ? "Run analysis" : runLabel}
          </Button>
        )}
      </div>
    </Card>
  );
}

export default RunAnalysisHeader;
